import React from 'react';
import { Container, Typography, Box, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  LocalShipping,
  VerifiedUser,
  AttachMoney,
  Inventory,
  SupportAgent,
  AutoAwesome,
  Storefront,
  Discount,
} from '@mui/icons-material';

const AmazonDropshippingSuppliers = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Page Title */}
        <Typography variant="h3" gutterBottom align="center" className="text-main-600 font-bold">
          Our Product Sourcing Partners
        </Typography>
        <Typography variant="body1" paragraph align="center">
          Blink Buy Dropshipping sources high-quality products from the world's leading e-commerce marketplaces. We provide sellers with access to millions of verified products across diverse categories, ensuring competitive pricing and reliable fulfillment through established global platforms.
        </Typography>
        <Typography variant="body2" paragraph align="center" sx={{ fontStyle: 'italic', color: '#666', maxWidth: '800px', margin: '0 auto' }}>
          <strong>Important Disclaimer:</strong> Blink Buy Dropshipping is not legally affiliated, endorsed, or partnered with Amazon, AliExpress, Alibaba, Walmart, or eBay. We source products from these public marketplaces through legitimate channels to provide our sellers with diverse inventory options.
        </Typography>

        {/* Supplier Partners Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold" sx={{ mt: 4 }}>
          Global Marketplace Integrations
        </Typography>
        <Grid container spacing={4}>
          {suppliers.map((supplier, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {supplier.name}
                </Typography>
                <List>
                  {supplier.features.map((feature, i) => (
                    <ListItem key={i} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <AutoAwesome fontSize="small" sx={{ color: 'text-main-600' }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Why Partner with Our Suppliers Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Why Partner with Our Suppliers?
        </Typography>
        <Grid container spacing={4}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box sx={{ fontSize: '3rem', color: 'text-main-600' }}>
                  {benefit.icon}
                </Box>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {benefit.title}
                </Typography>
                <Typography variant="body1">{benefit.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Efficient Multi-Supplier Management Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Efficient Multi-Supplier Management
        </Typography>
        <Typography variant="body1" paragraph>
          We handle multi-supplier integration to ensure:
        </Typography>
        <List>
          {managementFeatures.map((feature, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <VerifiedUser fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={feature} />
            </ListItem>
          ))}
        </List>

        {/* Call to Action Section */}
        <Box sx={{ mt: 6, textAlign: 'center', p: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
            Start Your Dropshipping Journey Today!
          </Typography>
          <Typography variant="body1" paragraph>
            With access to five major global marketplaces, you can source millions of products and build a thriving e-commerce business. Our platform streamlines the entire process from product selection to order fulfillment, allowing you to focus on marketing and growth.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            Ready to get started? Register your store and begin sourcing products from the world's leading marketplaces!
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

// Supplier Data
const suppliers = [
  {
    name: 'Amazon',
    features: [
      'World\'s largest online marketplace with millions of products',
      'Trusted brand recognition and customer confidence',
      'Fast Prime shipping options and reliable fulfillment',
      'Extensive product categories from electronics to home goods',
    ],
  },
  {
    name: 'AliExpress',
    features: [
      'Massive selection of affordable products from global suppliers',
      'Competitive pricing with direct manufacturer access',
      'Wide range of niche and trending products',
      'Established buyer protection and dispute resolution',
    ],
  },
  {
    name: 'Alibaba',
    features: [
      'Wholesale and bulk product sourcing opportunities',
      'Direct connection to manufacturers and factories',
      'Customization and private labeling options',
      'Ideal for scaling businesses with higher volume needs',
    ],
  },
  {
    name: 'Walmart',
    features: [
      'Trusted American brand with quality assurance',
      'Competitive pricing across diverse product categories',
      'Fast domestic shipping for U.S. customers',
      'Strong customer service and return policies',
    ],
  },
  {
    name: 'eBay',
    features: [
      'Established global marketplace with millions of sellers',
      'Unique and hard-to-find products',
      'Auction and fixed-price listing options',
      'Buyer protection programs for secure transactions',
    ],
  },
];

// Benefits Data
const benefits = [
  {
    icon: <LocalShipping sx={{ color: 'text-main-600' }} />,
    title: 'Faster Shipping',
    description: 'Meet Blink Buy customer expectations with quick and reliable delivery.',
  },
  {
    icon: <AttachMoney sx={{ color: 'text-main-600' }} />,
    title: 'Exclusive Pricing',
    description: 'Maximize profit margins with exclusive discounts and competitive pricing.',
  },
  {
    icon: <VerifiedUser sx={{ color: 'text-main-600' }} />,
    title: 'Premium Quality',
    description: 'Access high-quality products from verified suppliers.',
  },
  {
    icon: <SupportAgent sx={{ color: 'text-main-600' }} />,
    title: 'Dedicated Support',
    description: 'Get assistance from our dedicated support team for seamless operations.',
  },
  {
    icon: <Inventory sx={{ color: 'text-main-600' }} />,
    title: 'Automated Fulfillment',
    description: 'Streamline your business with automated tools and integrations.',
  },
  {
    icon: <Discount sx={{ color: 'text-main-600' }} />,
    title: 'Competitive Pricing',
    description: 'Optimize sales with competitive pricing strategies.',
  },
];

// Management Features Data
const managementFeatures = [
  'Product availability and inventory consistency',
  'Diversification to prevent stock shortages',
  'Competitive pricing strategies for optimized sales',
  'Automated tools for simplified supplier coordination',
];

export default AmazonDropshippingSuppliers;